import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE } from '../config/api';

const OfferListManagement = ({ park, onClose, onSuccess }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Get all offers and filter by park_id
      const response = await axios.get(`${API_BASE}/special-offers`, config);
      const parkOffers = response.data.offers.filter(o => o.park_id === park.id);
      setOffers(parkOffers);
    } catch (error) {
      console.error('Failed to fetch offers:', error);
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (offerId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const newStatus = currentStatus === 'active' ? 'dormant' : 'active';
      
      await axios.put(
        `${API_BASE}/admin/special-offers/${offerId}/status`,
        { status: newStatus },
        config
      );
      
      toast.success(`Offer marked as ${newStatus}`);
      fetchOffers();
      onSuccess();
    } catch (error) {
      console.error('Failed to toggle status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this offer? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API_BASE}/admin/special-offers/${offerId}`, config);
      
      toast.success('Offer deleted successfully');
      fetchOffers();
      onSuccess();
    } catch (error) {
      console.error('Failed to delete offer:', error);
      toast.error('Failed to delete offer');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gray-700 p-4 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-white">🗂️ Manage Offers</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl leading-none"
            >
              ×
            </button>
          </div>
          <p className="text-white text-opacity-90 text-sm mt-1">{park.name}</p>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <p className="text-center text-gray-500 py-8">Loading offers...</p>
          ) : offers.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No offers found for this location.</p>
          ) : (
            <div className="space-y-4">
              {offers.map((offer) => (
                <div
                  key={offer.id}
                  className={`border rounded-lg p-4 ${
                    offer.status === 'dormant' ? 'bg-gray-50 border-gray-300' : 'bg-white border-emerald-300'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-800">{offer.offer_title}</h3>
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        offer.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {offer.status.toUpperCase()}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">{offer.offer_description}</p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                    <span>Expires: {new Date(offer.expiration_date).toLocaleDateString()}</span>
                    <span>Redemptions: {offer.redemption_count}</span>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleToggleStatus(offer.id, offer.status)}
                      className={`flex-1 py-2 px-4 rounded font-medium text-sm transition-colors ${
                        offer.status === 'active'
                          ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                          : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                      }`}
                    >
                      {offer.status === 'active' ? '⏸️ Mark Dormant' : '▶️ Activate'}
                    </button>
                    <button
                      onClick={() => handleDelete(offer.id)}
                      className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded font-medium text-sm transition-colors"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-medium mt-6 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfferListManagement;
